import React, { useCallback, useEffect, useState } from "react";
import { Drawer } from "../../../common/comms/Drawer";
import { AvatarRing } from "../../../common/comms/AvatarRing";
import { MetricCard } from "../../../common/comms/MetricCard";
import { Toaster } from "../../../common/Toaster/Toaster";
import { getParticipants, patchAiSettings } from "../../../service/comms/roomsService";
import { listNotes } from "../../../service/comms/notesService";
import { get as commsGet, patch as commsPatch } from "../../../service/comms/commsApi";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";
import { participantId } from "../../../utils/commsParticipantId";

/** AI intervention modes rendered as a three-segment control. */
const AI_MODES = [
  { value: "ENABLED", label: "Enabled" },
  { value: "URGENCY_ONLY", label: "Urgency only" },
  { value: "DISABLED", label: "Off" },
];

const MAX_COACHES = 3;
const MAX_NOTES_PREVIEW = 2;

/**
 * Three-segment pill toggle. Active segment is brand-tinted; others are gray.
 *
 * @param {Object} props
 * @param {string} props.value - Currently selected mode value.
 * @param {(next: string) => void} props.onChange - Selection handler.
 * @param {boolean} [props.disabled] - Disable interaction while a change is in flight.
 */
const SegmentedControl = ({ value, onChange, disabled = false }) => (
  <div
    role="group"
    aria-label="AI intervention mode"
    className="inline-flex w-full rounded-lg border border-gray-200 p-0.5 bg-gray-50"
  >
    {AI_MODES.map((mode) => {
      const isActive = mode.value === value;
      return (
        <button
          key={mode.value}
          type="button"
          disabled={disabled}
          aria-pressed={isActive}
          onClick={() => !isActive && onChange(mode.value)}
          className={`flex-1 px-2 py-1.5 rounded-md text-xs font-semibold transition-colors ${
            isActive
              ? "bg-light-orange text-orange-primary border border-orange-200"
              : "text-gray-500 hover:text-gray-700"
          } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        >
          {mode.label}
        </button>
      );
    })}
  </div>
);

/** Section heading used across drawer blocks. */
const SectionTitle = ({ children }) => (
  <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">
    {children}
  </h3>
);

/**
 * Client details drawer: mini-profile, AI intervention control, coaching team,
 * shared notes, and AI activity metrics. Data loads only when open.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the drawer is open.
 * @param {() => void} props.onClose - Close handler.
 * @param {string} props.roomId - Room id to load details for.
 * @param {Object} [props.room] - Room record (status, ai_mode, plan, ai_activity).
 * @param {string} [props.clientName] - Client display name.
 * @param {() => void} [props.onOpenNotes] - Optional "view all notes" callback.
 */
export function DetailDrawer({ isOpen, onClose, roomId, room, clientName, onOpenNotes }) {
  const { getCoachName } = useCommsIdentity();
  const [aiMode, setAiMode] = useState(room?.ai_mode || "ENABLED");
  const [savingAi, setSavingAi] = useState(false);
  const [coaches, setCoaches] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setAiMode(room?.ai_mode || "ENABLED");
  }, [room?.ai_mode]);

  useEffect(() => {
    if (!isOpen || !roomId) return undefined;
    let active = true;
    setLoading(true);

    const load = async () => {
      try {
        const [participantsRes, notesRes] = await Promise.all([
          getParticipants(commsGet, roomId, true),
          listNotes(commsGet, roomId, false),
        ]);
        if (!active) return;
        const coachList = (participantsRes?.participants || []).filter(
          (p) => p.participant_role === "COACH" && p.removed_at == null
        );
        const noteList = (notesRes?.notes || []).slice().sort(
          (a, b) => Number(b.pinned) - Number(a.pinned)
        );
        setCoaches(coachList);
        setNotes(noteList);
      } catch (err) {
        console.error("Failed to load client details", err);
        if (active) {
          setCoaches([]);
          setNotes([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [isOpen, roomId]);

  const handleAiChange = useCallback(
    async (nextMode) => {
      const previous = aiMode;
      setAiMode(nextMode);
      setSavingAi(true);
      try {
        await patchAiSettings(commsPatch, roomId, nextMode);
        setToast({ message: "AI intervention updated", type: "success" });
      } catch (err) {
        console.error("Failed to update AI settings", err);
        setAiMode(previous);
        setToast({ message: "Could not update AI intervention", type: "error" });
      } finally {
        setSavingAi(false);
      }
    },
    [aiMode, roomId]
  );

  const activity = room?.ai_activity || {};
  const planName = room?.plan_name || room?.plan?.name;
  const atCoachLimit = coaches.length >= MAX_COACHES;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Client details">
      {/* 1. Client mini-profile */}
      <section className="flex items-center gap-3 pb-4 border-b border-gray-100">
        <AvatarRing name={clientName} size="lg" />
        <div className="min-w-0">
          <p className="text-base font-bold text-gray-800 truncate">
            {clientName || "Client"}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {room?.status || "Active"}
            {planName ? ` · ${planName}` : ""}
          </p>
        </div>
      </section>

      {/* 2. AI intervention */}
      <section className="py-4 border-b border-gray-100">
        <SectionTitle>AI intervention</SectionTitle>
        <SegmentedControl value={aiMode} onChange={handleAiChange} disabled={savingAi} />
        <p className="text-[11px] text-gray-400 mt-2">
          Room-wide &mdash; co-coaches see this change as a system event.
        </p>
        <p className="text-[11px] text-gray-500 mt-2">
          Reply SLA window &middot; 5 min &middot; +3 min while typing
        </p>
      </section>

      {/* 3. Coaching team */}
      <section className="py-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <SectionTitle>Coaching team</SectionTitle>
          <span className="text-xs text-gray-400">
            {coaches.length} of {MAX_COACHES}
          </span>
        </div>
        {loading ? (
          <p className="text-xs text-gray-400">Loading team&hellip;</p>
        ) : coaches.length === 0 ? (
          <p className="text-xs text-gray-400">No coaches assigned.</p>
        ) : (
          <ul className="space-y-2">
            {coaches.map((coach) => {
              const coachId = participantId(coach);
              const coachName = coach.name || getCoachName(coachId) || "Coach";
              return (
              <li key={coachId} className="flex items-center gap-2">
                <AvatarRing
                  name={coachName}
                  colorIndex={coach.coach_color_index}
                  presence={coach.presence}
                  size="sm"
                />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {coachName}
                  </p>
                  {coach.specialization && (
                    <p className="text-[11px] text-gray-400 truncate">
                      {coach.specialization}
                    </p>
                  )}
                </div>
              </li>
              );
            })}
          </ul>
        )}
        {atCoachLimit ? (
          <p className="text-[11px] text-gray-400 mt-3">
            Coaching team is at the {MAX_COACHES}-coach limit.
          </p>
        ) : (
          <button
            type="button"
            disabled
            title="admin approval flow"
            className="mt-3 w-full px-3 py-1.5 rounded-md text-xs font-semibold border border-gray-200 text-gray-400 cursor-not-allowed"
          >
            Request third coach
          </button>
        )}
      </section>

      {/* 4. Shared coach notes */}
      <section className="py-4 border-b border-gray-100">
        <SectionTitle>Shared coach notes</SectionTitle>
        {loading ? (
          <p className="text-xs text-gray-400">Loading notes&hellip;</p>
        ) : notes.length === 0 ? (
          <p className="text-xs text-gray-400">No shared notes yet.</p>
        ) : (
          <ul className="space-y-2">
            {notes.slice(0, MAX_NOTES_PREVIEW).map((note) => (
              <li
                key={note.id}
                className="rounded-lg bg-gray-50 border border-gray-100 p-2.5"
              >
                {note.pinned && (
                  <span className="text-[10px] font-bold uppercase text-orange-primary">
                    Pinned
                  </span>
                )}
                <p className="text-xs text-gray-700 line-clamp-3">{note.content}</p>
              </li>
            ))}
          </ul>
        )}
        {onOpenNotes && (
          <button
            type="button"
            onClick={onOpenNotes}
            className="mt-2 text-xs font-semibold text-orange-primary hover:underline"
          >
            View all in Notes
          </button>
        )}
      </section>

      {/* 5. AI activity this week */}
      <section className="py-4">
        <SectionTitle>AI activity this week</SectionTitle>
        <div className="grid grid-cols-3 gap-2">
          <MetricCard label="SLA bridges" value={activity.sla_bridges ?? 0} />
          <MetricCard label="Urgency replies" value={activity.urgency_replies ?? 0} />
          <MetricCard
            label="Deleted"
            value={activity.deleted ?? 0}
            tone="danger"
          />
        </div>
      </section>

      {toast?.message && <Toaster message={toast.message} type={toast.type} />}
    </Drawer>
  );
}
